import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, computed, effect, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AbstractDialog, AbstractDialogResultData, DialogComponent, DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { AgrI18nSharedModule, LocaleService } from '@next/core-lib/i18n';
import { startWith, Subject, takeUntil } from 'rxjs';

import { ImageDialogComponent } from 'src/app/shared/components/image-dialog/image-dialog.component';
import { CropFieldInfo, GrowthStageHistoryComponent } from '../growth-stage-history/growth-stage-history.component';
import {
  GrowthStageWarningDialogComponent,
  GrowthStageWarningDialogData,
} from './growth-stage-warning-dialog.component';
import { DateService } from '../../../shared/services/date.service';
import {
  BbchMajorStage,
  BbchStage,
  findByCode3Digit,
  formatBbchDisplayCode,
  getMajorStages,
  getMajorStageDescriptionKey,
  getStagesByMajorStage,
  isMajorStageEnabled,
} from '../../models/bbch-bulb-vegetable-scale';
import {
  CropFieldLatestGrowthStage,
  GrowthStageDialogData,
  GrowthStageRemovalRequest,
  GrowthStageRegistrationRequest,
} from '../../models/growth-stage.models';
import { DownyMildewSignalStore } from '../../signal-store/downy-mildew-signal-store';
import { ImmediateErrorStateMatcher } from 'src/app/shared/utils';

@Component({
  selector: 'app-growth-stage-dialog',
  templateUrl: './growth-stage-dialog.component.html',
  styleUrls: ['./growth-stage-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    MatTooltipModule,
    DialogComponent,
    AgrI18nSharedModule,
    GrowthStageHistoryComponent,
  ],
})
export class GrowthStageDialogComponent extends AbstractDialog<GrowthStageDialogData> implements OnInit, OnDestroy {
  data = inject<GrowthStageDialogData>(MAT_DIALOG_DATA);
  protected readonly mdDialogRef = inject<MatDialogRef<GrowthStageDialogComponent, AbstractDialogResultData<GrowthStageDialogData>>>(MatDialogRef);

  readonly downyMildewStore = inject(DownyMildewSignalStore);
  private readonly localeService = inject(LocaleService);
  private readonly dateService = inject(DateService);
  private readonly dialogService = inject(DialogService);
  private readonly matDialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly destroy$ = new Subject<void>();
  readonly isReadonlyMode = Boolean(this.data.readOnly);

  locale = '';

  readonly majorStages = getMajorStages().filter(isMajorStageEnabled);
  protected readonly formatBbchDisplayCode = formatBbchDisplayCode;
  filteredSubphases: BbchStage[] = [];
  latestGrowthStages: CropFieldLatestGrowthStage[] = [];
  minimumAllowedCode = '000';
  selectedCropFieldIds: number[] = [];
  isCodeTooLow = false;
  private defaultGrowthStageSelectionApplied = false;
  readonly maxDate = this.dateService.now();
  readonly dateErrorStateMatcher = new ImmediateErrorStateMatcher();
  private readonly hasSubmitted = signal(false);

  readonly registrationSucceeded = computed(() => {
    return (
      this.hasSubmitted() &&
      !this.downyMildewStore.growthStageRegistrationIsPending() &&
      !this.downyMildewStore.growthStageRegistrationError() &&
      this.downyMildewStore.growthStageRegistrationStatus() === 'success'
    );
  });

  form = new FormGroup({
    date: new FormControl<Date>((this.data.actionDate), { nonNullable: true, validators: [Validators.required] }),
    majorStage: new FormControl<BbchMajorStage | null>(null, [Validators.required]),
    subphase: new FormControl<BbchStage | null>(null, [Validators.required]),
  });

  constructor() {
    super();

    if (this.isReadonlyMode) {
      this.form.disable({ emitEvent: false });
    }

    // Update button state based on loading status changes
    effect(() => {
      this.downyMildewStore.growthStageRegistrationIsPending();
      this.updateButtonState();
      this.cdr.markForCheck();
    });

    // Close on successful submit
    effect(() => {
      if (this.registrationSucceeded()) {
        this.mdDialogRef.close({ status: DIALOG_RETURN_TYPES.confirm });
      }
    });

    // Watch for latest growth stages changes and update minimum allowed code
    effect(() => {
      const stages = this.downyMildewStore.latestGrowthStagesValue();

      this.latestGrowthStages = stages;
      this.updateMinimumAllowedCode();
      this.applyDefaultGrowthStageSelection();
      this.updateButtonState();
      this.cdr.markForCheck();
    });

  }

  ngOnInit(): void {
    this.selectedCropFieldIds = this.data.advices.map((a) => a.cropFieldId);

    // Submit action is rendered inline in dialog content
    this.data.confirmButton = false;

    this.localeService.localeDataChanged$.pipe(startWith(undefined), takeUntil(this.destroy$)).subscribe(() => {
      this.locale = this.localeService.getLocale();
    });

    this.form.controls.majorStage.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((majorStage) => {
      if (majorStage !== null) {
        this.filteredSubphases = getStagesByMajorStage(majorStage);
        this.form.controls.subphase.reset(null);
        this.isCodeTooLow = false;
      } else {
        this.filteredSubphases = [];
      }
    });

    this.form.controls.subphase.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((subphase) => {
      if (subphase) {
        this.isCodeTooLow = Number.parseInt(subphase.code3Digit, 10) < Number.parseInt(this.minimumAllowedCode, 10);
      } else {
        this.isCodeTooLow = false;
      }
      this.updateButtonState();
    });

    // Watch for form changes to update button state
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateButtonState();
    });

    this.updateGrowthStageSelection();
  }

  private updateButtonState(): void {
    this.data.disableConfirmButton = this.isSubmitDisabled;
  }

  get isSubmitDisabled(): boolean {
    return (
      this.isReadonlyMode ||
      !this.form.valid ||
      this.isCodeTooLow ||
      this.downyMildewStore.growthStageRegistrationIsPending() ||
      this.selectedCropFieldIds.length === 0
    );
  }

  get isSubmitting(): boolean {
    return this.downyMildewStore.growthStageRegistrationIsPending();
  }

  getMajorStageKey(majorStage: BbchMajorStage): string {
    return getMajorStageDescriptionKey(majorStage);
  }

  getMajorStageImagePath(majorStage: BbchMajorStage): string {
    return `assets/images/bbch/bbch-stage-0${majorStage}.png`;
  }

  onInfoClicked(majorStage: BbchMajorStage): void {
    this.matDialog.open(ImageDialogComponent, {
      data: {
        imagePath: this.getMajorStageImagePath(majorStage),
        titleTranslationKey: this.getMajorStageKey(majorStage),
      },
      maxWidth: '90vw',
      maxHeight: '90vh',
    });
  }

  removeCropField(cropFieldId: number): void {
    if (this.isReadonlyMode) {
      return;
    }

    this.selectedCropFieldIds = this.selectedCropFieldIds.filter((id) => id !== cropFieldId);
    this.updateMinimumAllowedCode();
    this.updateGrowthStageSelection();
    // Re-validate the current selection after removing a crop field
    const currentSubphase = this.form.controls.subphase.value;
    if (currentSubphase) {
      this.isCodeTooLow = this.isSubphaseDisabled(currentSubphase);
    }
    this.updateButtonState();
  }

  getCropFieldName(cropFieldId: number): string {
    const advice = this.data.advices.find((a) => a.cropFieldId === cropFieldId);
    return advice?.cropFieldName ?? '';
  }

  getCropFieldArea(cropFieldId: number): number {
    const advice = this.data.advices.find((a) => a.cropFieldId === cropFieldId);
    return advice?.amountOfHectares ?? 0;
  }

  get selectedCropFieldInfos(): CropFieldInfo[] {
    return this.selectedCropFieldIds.map((cropFieldId) => ({
      id: cropFieldId,
      name: this.getCropFieldName(cropFieldId),
    }));
  }

  isSubphaseDisabled(subphase: BbchStage): boolean {
    const code = Number.parseInt(subphase.code3Digit, 10);
    const minCode = Number.parseInt(this.minimumAllowedCode, 10);
    return code < minCode;
  }

  isMajorStageDisabled(majorStage: BbchMajorStage): boolean {
    const substages = getStagesByMajorStage(majorStage);
    return substages.length > 0 && substages.every((s) => this.isSubphaseDisabled(s));
  }

  submitGrowthStage(): void {
    if (this.isSubmitDisabled) {
      return;
    }

    const subphase = this.form.controls.subphase.value!;
    const date = this.form.controls.date.value;
    const selectedCode = Number.parseInt(subphase.code3Digit, 10);

    // Filter crop fields based on whether the new code is higher than or equal to their current stage
    const allowedCropFields: number[] = [];
    const excludedCropFields: Array<{ cropFieldId: number; cropFieldName: string; currentCode: string }> = [];

    this.selectedCropFieldIds.forEach((cropFieldId) => {
      const currentStage = this.latestGrowthStages.find((gs) => gs.cropFieldId === cropFieldId);
      const currentCode = currentStage?.latestBbchCode;

      // If no current code (null), always allow
      if (currentCode === null || currentCode === undefined) {
        allowedCropFields.push(cropFieldId);
      } else {
        const currentCodeNum = Number.parseInt(currentCode, 10);
        if (selectedCode >= currentCodeNum) {
          allowedCropFields.push(cropFieldId);
        } else {
          excludedCropFields.push({
            cropFieldId,
            cropFieldName: this.getCropFieldName(cropFieldId),
            currentCode: currentCode,
          });
        }
      }
    });

    // If some crop fields would be excluded, show warning dialog
    if (excludedCropFields.length > 0) {
      const dialogData: GrowthStageWarningDialogData = {
        selectedCode: subphase.code3Digit,
        excludedCropFields,
        allowedCropFields: allowedCropFields.map((id) => ({
          cropFieldId: id,
          cropFieldName: this.getCropFieldName(id),
        })),
      };

      const dialogRef = this.matDialog.open(GrowthStageWarningDialogComponent, {
        data: dialogData,
        width: '500px',
        maxWidth: '90vw',
        disableClose: false,
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === 'continue' && allowedCropFields.length > 0) {
          this.registerGrowthStageForCropFields(allowedCropFields, subphase.code3Digit, date);
        }
      });
    } else {
      // All crop fields are allowed, proceed normally
      this.registerGrowthStageForCropFields(allowedCropFields, subphase.code3Digit, date);
    }
  }

  onRemoveGrowthStage(request: GrowthStageRemovalRequest): void {
    if (this.isReadonlyMode) {
      return;
    }

    this.dialogService
      .confirm({
        title: this.localeService.translate('downyMildew.growthStageHistory.deleteConfirmMessage'),
        pushHistoryState: false,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          this.downyMildewStore.removeGrowthStage(request);
        }
      });
  }

  private registerGrowthStageForCropFields(cropFieldIds: number[], bbchCode: string, date: Date): void {
    const request: GrowthStageRegistrationRequest = {
      cropFieldIds,
      bbchCode,
      registrationDate: this.formatDate(date),
    };

    this.hasSubmitted.set(true);
    this.downyMildewStore.registerGrowthStage(request);
  }

  private updateGrowthStageSelection(): void {
    this.downyMildewStore.setGrowthStageSelection(this.selectedCropFieldIds, this.data.cropYear);
  }

  private updateMinimumAllowedCode(): void {
    const relevantStages = this.latestGrowthStages.filter((gs) => this.selectedCropFieldIds.includes(gs.cropFieldId));

    if (relevantStages.length === 0) {
      this.minimumAllowedCode = '000';
      return;
    }

    // Convert all codes to numbers, treating null as 0
    // Find the MINIMUM code across all selected crop fields
    // This ensures we only disable codes that cannot be applied to ANY crop field
    const codes = relevantStages.map((gs) => {
      const codeString = gs.latestBbchCode !== null ? gs.latestBbchCode : '000';
      return Number.parseInt(codeString, 10);
    });

    const minCode = Math.min(...codes);
    this.minimumAllowedCode = minCode.toString().padStart(3, '0');

    this.resetSelectionWhenMajorStageIsDisabled();
  }

  private applyDefaultGrowthStageSelection(): void {
    if (this.defaultGrowthStageSelectionApplied) {
      return;
    }

    if (this.form.controls.majorStage.value || this.form.controls.subphase.value) {
      this.defaultGrowthStageSelectionApplied = true;
      return;
    }

    const relevantStages = this.latestGrowthStages.filter((gs) => this.selectedCropFieldIds.includes(gs.cropFieldId));
    const hasAnyGrowthStage = relevantStages.some((stage) => stage.latestBbchCode);

    if (hasAnyGrowthStage) {
      const defaultMajorStage = this.getDefaultMajorStageFromExistingGrowthStages(relevantStages);
      if (defaultMajorStage !== undefined) {
        this.form.controls.majorStage.setValue(defaultMajorStage);
      }

      this.defaultGrowthStageSelectionApplied = true;
      return;
    }

    if (!this.data.preferredBbchCode) {
      this.defaultGrowthStageSelectionApplied = true;
      return;
    }

    const defaultSubphase = findByCode3Digit(this.data.preferredBbchCode);
    if (!defaultSubphase) {
      return;
    }

    this.form.controls.majorStage.setValue(defaultSubphase.majorStage);
    this.form.controls.subphase.setValue(defaultSubphase);
    this.defaultGrowthStageSelectionApplied = true;
  }

  private resetSelectionWhenMajorStageIsDisabled(): void {
    // Major stages remain selectable; validation is enforced at subphase and submit level.
  }

  private getDefaultMajorStageFromExistingGrowthStages(
    stages: CropFieldLatestGrowthStage[],
  ): BbchMajorStage | undefined {
    const normalizedCodes = stages
      .map((stage) => this.normalizeBbchCode(stage.latestBbchCode))
      .filter((code): code is string => !!code);

    if (!normalizedCodes.length) {
      return undefined;
    }

    const sortedCodes = [...normalizedCodes].sort(
      (left, right) => Number.parseInt(left, 10) - Number.parseInt(right, 10),
    );
    const lowestExistingCode = sortedCodes[0];
    const matchingStage = findByCode3Digit(lowestExistingCode);
    return matchingStage?.majorStage;
  }

  private normalizeBbchCode(bbchCode: string | null): string | undefined {
    if (!bbchCode) {
      return undefined;
    }

    const digits = bbchCode.replace(/\D/g, '');
    if (!digits) {
      return undefined;
    }

    if (digits.length >= 3) {
      return digits.slice(-3);
    }

    if (digits.length === 2) {
      return `${digits[0]}0${digits[1]}`;
    }

    return `00${digits}`;
  }

  private formatDate(date: Date): string {
    // Coerce to native Date to support MomentDateAdapter returning a Moment object from the datepicker
    const nativeDate = new Date(+date);
    const day = nativeDate.getDate().toString().padStart(2, '0');
    const month = (nativeDate.getMonth() + 1).toString().padStart(2, '0');
    const year = nativeDate.getFullYear();
    return `${year}-${month}-${day}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
